import { useCallback, useMemo, useState } from 'react';
import { Form, TablePaginationConfig } from 'antd';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import { usePurchaseOrderItemsListQuery } from 'api';
import { ItemFilters } from './types';
import { TableProps } from 'components';
import { ItemDataType } from 'pages/admin/items';

type OrderType = 'asc' | 'desc';

interface UsePurchaseOrderItemsProps {
  purchaseOrderId: string;
}

const debounce = (func: (...args: any[]) => void, wait: number) => {
  let timeout: NodeJS.Timeout;
  return (...args: any[]) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

export const usePurchaseOrderItems = ({ purchaseOrderId }: UsePurchaseOrderItemsProps) => {
  const [form] = Form.useForm();
  const [pagination, setPagination] = useState({ page: 1, size: 10 });
  const [sorting, setSorting] = useState<{ sortField?: string; sortOrder?: OrderType }>({});
  const [search, setSearch] = useState<string>('');
  const [filters, setFilters] = useState<ItemFilters>({});
  const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);

  // Create a stable debounced search function
  const debouncedSetSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearch(value);
        setPagination((prev) => ({ ...prev, page: 1 }));
      }, 300),
    []
  );

  const { data, isLoading } = usePurchaseOrderItemsListQuery(
    {
      purchaseOrderId,
      ...pagination,
      ...sorting,
      search: search || undefined,
      filters,
    },
    {
      skip: !purchaseOrderId,
    }
  );

  const totalElements = data?.totalElements || 0;

  const handleTableChange: TableProps<ItemDataType>['onChange'] = useCallback(
    (
      pagination: TablePaginationConfig,
      _filters: Record<string, FilterValue | null>,
      sorter: SorterResult<ItemDataType> | SorterResult<ItemDataType>[]
    ) => {
      const sortField = Array.isArray(sorter) ? sorter[0].columnKey : sorter.columnKey;
      const sortOrder = Array.isArray(sorter) ? sorter[0].order : sorter.order;
      const order = sortOrder === 'ascend' ? 'asc' : sortOrder === 'descend' ? 'desc' : undefined;
      setPagination({
        page: pagination.current || 1,
        size: pagination.pageSize || 10,
      });
      setSorting({
        sortField: sortField as string,
        sortOrder: order as OrderType,
      });
    },
    []
  );

  const onSearch = useCallback(
    (value: string) => {
      debouncedSetSearch(value);
    },
    [debouncedSetSearch]
  );

  const openFilterDrawer = useCallback(() => {
    setFilterDrawerVisible(true);
  }, []);

  const closeFilterDrawer = useCallback(() => {
    setFilterDrawerVisible(false);
  }, []);

  const applyFilters = useCallback(
    (values: ItemFilters) => {
      setFilters(values);
      setPagination({ ...pagination, page: 1 });
      closeFilterDrawer();
    },
    [pagination, closeFilterDrawer]
  );

  const resetFilters = useCallback(() => {
    form.resetFields();
    setFilters({});
    setPagination({ ...pagination, page: 1 });
  }, [form, pagination]);

  return {
    data: data?.content || [],
    isLoading,
    pagination: { ...pagination, total: totalElements },
    form,
    handleTableChange,
    onSearch,
    filterDrawerVisible,
    openFilterDrawer,
    closeFilterDrawer,
    applyFilters,
    resetFilters,
    totalElements,
  };
};
